import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronDown, ChevronUp, Download, Calendar, Users, Info } from 'lucide-react';
import { useVenue } from '../../contexts/VenueContext';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { useBackTarget } from '../../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const font = "'Inria Serif', serif";
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS_HDR = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const getFirstDayOfWeek = (y, m) => (new Date(y, m, 1).getDay() + 6) % 7;

const cardStyle = {
  backgroundColor: 'var(--app-bg)',
  borderRadius: 18,
  border: '1px solid var(--card-border)',
  boxShadow: 'var(--premium-shadow-main)',
};

const formatEuro = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')}€`;

function MiniCalendar({ selectedDate, onSelect, onClose }) {
  const d = selectedDate ? new Date(selectedDate) : new Date();
  const [vy, setVy] = useState(d.getFullYear());
  const [vm, setVm] = useState(d.getMonth());
  const dim = getDaysInMonth(vy, vm);
  const fd = getFirstDayOfWeek(vy, vm);
  const cells = [];
  for (let i = 0; i < fd; i++) cells.push(null);
  for (let i = 1; i <= dim; i++) cells.push(i);
  const makeDateStr = (day) => `${vy}-${String(vm + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  return (
    <div style={{ ...cardStyle, padding: 12, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <button type="button" onClick={() => { if (vm === 0) { setVm(11); setVy((y) => y - 1); } else setVm((m) => m - 1); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: font, fontSize: 20 }}>‹</button>
        <span style={{ fontFamily: font, fontSize: 16, fontWeight: 700 }}>{MONTHS_FULL[vm]} {vy}</span>
        <button type="button" onClick={() => { if (vm === 11) { setVm(0); setVy((y) => y + 1); } else setVm((m) => m + 1); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: font, fontSize: 20 }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {DAYS_HDR.map((dd) => (
          <div key={dd} style={{ textAlign: 'center', fontFamily: font, fontSize: 12, color: 'var(--text-3)', padding: 2 }}>{dd}</div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`empty-${vy}-${vm}-${i}`} />;
          const ds = makeDateStr(day);
          const isSel = ds === selectedDate;
          return (
            <button
              key={ds}
              type="button"
              onClick={() => { onSelect(ds); onClose(); }}
              style={{
                width: '100%', aspectRatio: '1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: font, fontSize: 14, fontWeight: isSel ? 700 : 400,
                color: isSel ? '#fff' : 'var(--text)',
                backgroundColor: isSel ? 'var(--accent-purple-strong)' : 'transparent',
                borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0,
              }}
            >
              {day}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
        <button type="button" onClick={() => { onSelect(null); onClose(); }}
          style={{ fontFamily: font, fontSize: 14, color: 'var(--accent-purple-strong)', background: 'none', border: 'none', cursor: 'pointer' }}>
          Show all dates
        </button>
      </div>
    </div>
  );
}

export default function VenuePaymentsHistoryPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const { activeVenue } = useVenue();
  const [data, setData] = useState({ transactions: [], totals: { profit: 0, bills: 0, staff_tips: 0, count: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);

  const venueId = activeVenue?.live_venue_id || activeVenue?.venue_id || null;

  useEffect(() => {
    if (!venueId) { setLoading(false); return undefined; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/transactions/venue/${venueId}/history`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: 'PAID' },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Could not load payment history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [venueId]);

  // Group by ISO date (YYYY-MM-DD)
  const grouped = useMemo(() => {
    const out = {};
    for (const txn of data.transactions || []) {
      const day = (txn.created_at || '').slice(0, 10);
      if (!day) continue;
      if (selectedDate && day !== selectedDate) continue;
      if (!out[day]) out[day] = [];
      out[day].push(txn);
    }
    return out;
  }, [data.transactions, selectedDate]);

  const sortedDates = useMemo(
    () => Object.keys(grouped).sort((a, b) => b.localeCompare(a)),
    [grouped],
  );

  const filteredCount = useMemo(
    () => Object.values(grouped).reduce((s, arr) => s + arr.length, 0),
    [grouped],
  );
  const filteredProfit = useMemo(
    () => Object.values(grouped).reduce(
      (s, arr) => s + arr.reduce((acc, t) => acc + Number(t.venue_profit || 0), 0),
      0,
    ),
    [grouped],
  );

  const filteredTips = useMemo(
    () => Object.values(grouped).reduce(
      (s, arr) => s + arr.reduce((acc, t) => acc + Number(t.tip_amount || 0), 0),
      0,
    ),
    [grouped],
  );

  const formatDate = (d) => {
    const [y, m, dd] = d.split('-');
    return `${dd}/${m}/${y}`;
  };

  return (
    <div className="app-shell" data-testid="venue-payments-history-page" style={{
      minHeight: '100vh', backgroundColor: 'var(--app-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 40,
    }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'flex-start', padding: '16px 20px 0' }}>
        <button type="button" onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <ChevronLeft size={30} color="var(--text)" strokeWidth={2} />
        </button>
      </div>
      <div style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={100} height={89} dataTestId="venue-payments-history-shared-logo" />
      </div>
      <h2 style={{ fontFamily: font, fontSize: 26, fontWeight: 700, color: 'var(--text)', marginTop: 8, marginBottom: 8 }}>Payouts History</h2>

      {/* Summary card with filter */}
      <div style={{ ...cardStyle, width: 'calc(100% - 32px)', maxWidth: 360, padding: '14px 16px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-3)', margin: 0, letterSpacing: 0.4, textTransform: 'uppercase' }}>
            {selectedDate ? formatDate(selectedDate) : 'All paid transactions'}
          </p>
          <p data-testid="venue-payments-total-profit" style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: 'var(--status-success-text)', margin: '4px 0' }}>
            {formatEuro(selectedDate ? filteredProfit : data.totals?.profit)}
          </p>
          <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-3)', margin: 0 }}>
            {selectedDate ? filteredCount : data.totals?.count || 0} {((selectedDate ? filteredCount : data.totals?.count) === 1) ? 'transaction' : 'transactions'}
          </p>
        </div>
        <button type="button" onClick={() => setShowCalendar(!showCalendar)}
          data-testid="venue-payments-filter-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: font, fontSize: 13, color: 'var(--text)', background: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '8px 12px', cursor: 'pointer' }}>
          <Calendar size={16} color="var(--text)" />
          {selectedDate ? 'Date' : 'Filter'}
        </button>
      </div>

      {/* Monthly staff tips summary — always shown so venue owners know about tips */}
      {!loading && !error && (
        <div
          data-testid="venue-payments-tips-card"
          style={{
            ...cardStyle,
            width: 'calc(100% - 32px)', maxWidth: 360, padding: '14px 16px', marginBottom: 12,
            background: 'linear-gradient(135deg, rgba(0, 179, 65, 0.06) 0%, rgba(0, 179, 65, 0.02) 100%)',
            border: '1px solid rgba(0, 179, 65, 0.2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(0, 179, 65, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users size={18} color="#00B341" />
              </div>
              <div>
                <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-3)', margin: 0, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                  {selectedDate ? formatDate(selectedDate) : 'Total'} Staff Tips
                </p>
                <p data-testid="venue-payments-total-tips" style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: '#00B341', margin: '2px 0 0' }}>
                  {formatEuro(selectedDate ? filteredTips : data.totals?.staff_tips)}
                </p>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 10, padding: '8px 10px', background: 'rgba(0, 0, 0, 0.04)', borderRadius: 10 }}>
            <Info size={13} color="var(--text-3)" style={{ flexShrink: 0, marginTop: 2 }} />
            <p style={{ fontFamily: font, fontSize: 12, color: 'var(--text-3)', margin: 0, lineHeight: 1.4 }}>
              Tips pass directly to your venue account. As the venue, you are responsible for distributing them to staff and for any applicable tax obligations on tips.
            </p>
          </div>
        </div>
      )}

      {showCalendar && (
        <div style={{ width: '100%', maxWidth: 360, padding: '0 16px' }}>
          <MiniCalendar selectedDate={selectedDate} onSelect={setSelectedDate} onClose={() => setShowCalendar(false)} />
        </div>
      )}

      <div style={{ width: '100%', maxWidth: 360, padding: '0 16px' }}>
        {loading && (
          <p style={{ fontFamily: font, fontSize: 15, color: 'var(--text-3)', textAlign: 'center', marginTop: 40 }}>Loading…</p>
        )}
        {!loading && error && (
          <p style={{ fontFamily: font, fontSize: 14, color: 'var(--status-danger-text)', textAlign: 'center', marginTop: 40 }}>{error}</p>
        )}
        {!loading && !error && sortedDates.length === 0 && (
          <p style={{ fontFamily: font, fontSize: 16, color: 'var(--text-3)', textAlign: 'center', marginTop: 60 }}>
            {selectedDate ? 'No transactions on this day' : 'No paid transactions yet'}
          </p>
        )}
        {!loading && !error && sortedDates.map((date) => (
          <div key={date} style={{ marginBottom: 12 }}>
            <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-3)', margin: '8px 0 6px' }}>{formatDate(date)}</p>
            {grouped[date].map((p) => {
              const isOpen = expandedId === p.id;
              const allureFee = Number(p.app_fee || 0) + Number(p.host_commission || 0);
              return (
                <div key={p.id} style={{ ...cardStyle, padding: '12px 14px', marginBottom: 8 }}>
                  <div onClick={() => setExpandedId(isOpen ? null : p.id)}
                    role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') setExpandedId(isOpen ? null : p.id); }}
                    style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', cursor: 'pointer' }}>
                    <div>
                      <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                        Customer · <span style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{p.customer_id || '—'}</span>
                      </p>
                      <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-3)', margin: '2px 0' }}>
                        Bill {formatEuro(p.original_amount)}
                        {Number(p.tip_amount) > 0 && <> · Tip {formatEuro(p.tip_amount)}</>}
                      </p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <span style={{ fontFamily: font, fontSize: 17, fontWeight: 700, color: 'var(--status-success-text)' }}>
                        {formatEuro(p.venue_profit)}
                      </span>
                      {isOpen ? <ChevronUp size={18} color="var(--text)" /> : <ChevronDown size={18} color="var(--text)" />}
                    </div>
                  </div>
                  {isOpen && (
                    <div data-testid={`payment-detail-${p.id}`} style={{ marginTop: 10, borderTop: '1px solid var(--hairline)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <DetailRow label="Original bill" value={formatEuro(p.original_amount)} />
                      <DetailRow
                        label={`Allure discount · ${Math.round(p.discount_percent || 0)}%`}
                        value={`− ${formatEuro((p.original_amount || 0) - (p.discounted_amount || 0))}`}
                        valueColor="#1F8E2A"
                      />
                      <DetailRow label="Customer paid" value={formatEuro(p.discounted_amount)} />
                      <div style={{ height: 1, background: 'var(--hairline)', margin: '4px 0' }} />
                      <DetailRow label="Viva Wallet fee" value={`− ${formatEuro(p.viva_wallet_fee)}`} />
                      <DetailRow label="Allure fee" value={`− ${formatEuro(allureFee)}`} />
                      <div style={{ height: 1, background: 'var(--hairline)', margin: '4px 0' }} />
                      <DetailRow label="Net to your account" value={formatEuro(p.venue_profit)} valueColor="#1F8E2A" bold />
                      {Number(p.tip_amount) > 0 && (
                        <DetailRow
                          label={`Staff tip (${p.tip_percent}%) · passes through`}
                          value={`+ ${formatEuro(p.tip_amount)}`}
                          valueColor="#1F8E2A"
                        />
                      )}
                      <button type="button" data-testid={`payment-receipt-${p.id}`}
                        style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        <Download size={16} color="var(--accent-purple)" />
                        <span style={{ fontFamily: font, fontSize: 14, color: 'var(--accent-purple)' }}>Download receipt</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function DetailRow({ label, value, valueColor, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontFamily: font, fontSize: 13, color: 'var(--text-3)' }}>{label}</span>
      <span style={{ fontFamily: font, fontSize: 14, fontWeight: bold ? 700 : 500, color: valueColor || 'var(--text)' }}>
        {value}
      </span>
    </div>
  );
}
